import { motion } from 'framer-motion';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import styled from 'styled-components';
import { useFramerAnimation } from '../../../hooks';
import {
  AnchorText,
  Col,
  Container,
  Grid,
  Overlay,
  SanityBlockContent,
  Section,
  SectionHeader,
} from '../../common';
import { excludeDrafts } from '../../../utils';

const StyledCol = styled(Col)`
  border-top-width: 4px;
  border-top-color: ${props => props.$borderTopColor};
`;

const StyledH3 = styled(motion.h3)`
  color: ${props => props.$color} !important;
  &:hover {
    color: ${props => props.$colorHover} !important;
  }
`;

const getListLength = (listType, services, pages) =>
  listType === 'serviceList'
    ? services?.length
    : pages?.filter(page => page?.type === 'service')?.length;

const getGridClasses = (listLength, noServiceSectionImage, displayShortDescription) => {
  if (displayShortDescription) {
    return 'lg:grid-cols-3 gap-x-8 gap-y-12';
  }
  return listLength > 4
    ? 'lg:grid-cols-4 gap-x-8 gap-y-12'
    : noServiceSectionImage
      ? 'gap-12 lg:grid-cols-2'
      : 'gap-12';
};

const ServiceItemShort = ({ item, animationProps, colors, sanity, listHeadlineSize }) => (
  <StyledCol
    key={item?.slug?.current}
    {...animationProps}
    $borderTopColor={colors?.accent?.default}
    classes="overflow-hidden rounded-xl border shadow-md transition-transform duration-300 hover:shadow-2xl hover:-translate-y-1 bg-white border-t-4 border-t-amber-500"
  >
    <GatsbyImage
      image={item?.image?.asset?.gatsbyImageData}
      alt={item?.image?.alt}
      className="object-cover w-full aspect-[4/3]"
    />
    <div className="p-6 text-center">
      <StyledH3
        $color={colors?.neutral?.darker}
        $colorHover={colors?.accent?.default}
        className={`${listHeadlineSize} font-bold`}
      >
        {item?.name}
      </StyledH3>
      {item?._rawShortDescription && (
        <div className="mt-4 text-lg text-neutral-700">
          <SanityBlockContent sanity={sanity} blocks={item?._rawShortDescription} />
        </div>
      )}
    </div>
  </StyledCol>
);

const ServiceItemDefault = ({
  item,
  animationProps,
  colors,
  sanity,
  listHeadlineSize,
  noServiceSectionImage,
}) => (
  <Col key={item?.slug?.current} {...animationProps} className="relative">
    <motion.div
      className="relative aspect-[1/1] overflow-hidden rounded-xl hover:shadow-2xl"
      whileHover={{ scale: 1.05 }}
    >
      <AnchorText
        sanity={sanity}
        type="internal"
        to={item?.link?.slug?.current || item?.slug?.current}
        className="absolute inset-0 flex items-center justify-center px-8"
      >
        <div className="absolute inset-0 z-0">
          <GatsbyImage
            image={item?.image?.asset?.gatsbyImageData}
            alt={item?.image?.alt}
            className="object-cover w-full h-full"
          />
          <Overlay
            overlayType={colors?.hero?.overlayImageType}
            overlayAngle={colors?.hero?.overlayImageAngle}
            overlayWidth={colors?.hero?.overlayImageWidth}
            overlayHeight={colors?.hero?.overlayImageHeight}
            bgColor={colors?.neutral?.default}
            bgColorHover={colors?.neutral?.dark}
            bgColorStart={colors?.neutral?.darker}
            bgColorMid={colors?.neutral?.dark}
            bgColorEnd={colors?.neutral?.default}
          />
        </div>

        <div className="relative z-10 flex items-center justify-center w-full h-full px-4 text-center">
          <StyledH3
            $color={colors?.neutral?.white}
            $colorHover={colors?.accent?.light}
            className={`${listHeadlineSize} font-bold`}
          >
            <span className="drop-shadow-darker">{item?.name}</span>
          </StyledH3>
        </div>
      </AnchorText>
    </motion.div>
  </Col>
);

const ServiceItem = ({
  item,
  index,
  displayShortDescription,
  colors,
  sanity,
  listHeadlineSize,
  noServiceSectionImage,
}) => {
  const animationProps = useFramerAnimation({
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, delay: index * 0.15, ease: 'easeOut' },
    inViewConfig: { once: true, margin: '10% 0%' },
  });

  return displayShortDescription ? (
    <ServiceItemShort
      item={item}
      animationProps={animationProps}
      colors={colors}
      sanity={sanity}
      listHeadlineSize={listHeadlineSize}
    />
  ) : (
    <ServiceItemDefault
      item={item}
      animationProps={animationProps}
      colors={colors}
      sanity={sanity}
      listHeadlineSize={listHeadlineSize}
      noServiceSectionImage={noServiceSectionImage}
    />
  );
};

const ServicesOverview = ({ sanity, section }) => {
  const { colors, services, pages } = sanity;
  const noServiceSectionImage = !section?.image?.asset?.gatsbyImageData;
  const listHeadlineSize = section?.listHeadlineSize?.fontSize || 'text-3xl';
  const displayShortDescription = section?.displayShortDescription;

  // ✅ Filter out drafts before sorting
  const servicesToDisplay =
    section?.listType === 'serviceList'
      ? excludeDrafts([...services]).sort((a, b) => a?.order - b?.order)
      : excludeDrafts([...pages])
          .filter(page => page?.type === 'service')
          .sort((a, b) => a?.order - b?.order);

  const listLength = servicesToDisplay.length;
  const showImage = listLength <= 4 && !noServiceSectionImage;
  const gridClasses = getGridClasses(listLength, noServiceSectionImage, displayShortDescription);

  return (
    <Section
      idName="services-overview"
      bgColor={colors?.neutral?.lighter}
      h2Color={colors?.neutral?.darker}
    >
      <Container padding="none" classes="max-w-md px-4 text-center sm:max-w-3xl lg:max-w-7xl">
        <SectionHeader
          sanity={sanity}
          tagline={section?.header?.tagline}
          headline={section?.header?.headline}
          description={section?.header?._rawDescription}
        />
        <Grid classes="lg:grid-cols-2 gap-24 flex justify-center items-center">
          {showImage && (
            <Col classes="hidden lg:block w-full">
              <GatsbyImage
                image={section?.image?.asset?.gatsbyImageData}
                alt={section?.image?.alt}
                className="drop-shadow-2xl"
              />
            </Col>
          )}
          <Col classes={showImage ? '' : 'lg:col-span-2'}>
            <Grid classes={gridClasses}>
              {servicesToDisplay?.map((item, index) => (
                <ServiceItem
                  key={item?.slug?.current}
                  item={item}
                  index={index}
                  displayShortDescription={displayShortDescription}
                  colors={colors}
                  sanity={sanity}
                  listHeadlineSize={listHeadlineSize}
                  noServiceSectionImage={noServiceSectionImage}
                />
              ))}
            </Grid>
          </Col>
        </Grid>
      </Container>
    </Section>
  );
};

export { ServicesOverview };
